import { parseChecklistContent, checklistToMarkdown } from './checklist-parser'

/**
 * transform-interactive-components.ts
 *
 * Utility for transforming interactive component tags in chat messages to markdown format
 * when copying to clipboard.
 *
 * Primary responsibilities:
 * - Transform special component tags like [FLASHCARD], [QUIZ], etc. to markdown
 * - Preserve the content's meaning and structure in plain text
 * - Support all interactive components used in the chat interface
 */

/**
 * Transforms interactive component tags in content to markdown format for clipboard copying
 */
export function transformInteractiveComponentsToMarkdown(content: string): string {
  if (!content) return content

  let transformedContent = content

  // Process flashcards - [FLASHCARD]question|answer[/FLASHCARD]
  transformedContent = transformedContent.replace(
    /\[FLASHCARD\](.*?)\|(.*?)\[\/FLASHCARD\]/gs,
    (_, question, answer) => {
      return `📝 FLASHCARD\nQuestion: ${question.trim()}\nAnswer: ${answer.trim()}\n\n`
    }
  )

  // Process todo lists - [TODO]item1|item2|item3[/TODO]
  transformedContent = transformedContent.replace(/\[TODO\](.*?)\[\/TODO\]/gs, (_, items) => {
    const todoItems = items
      .split('|')
      .map((item: string) => `□ ${item.trim()}`)
      .join('\n')
    return `📋 TODO LIST\n${todoItems}\n\n`
  })

  // Process quizzes - [QUIZ]question|option1|option2|correct|explanation[/QUIZ]
  transformedContent = transformedContent.replace(
    /\[QUIZ\](.*?)\|(.*?)\|(.*?)\|(.*?)\|(.*?)\[\/QUIZ\]/gs,
    (_, question, ...rest) => {
      const options = rest.slice(0, -2)
      const correctAnswer = rest[rest.length - 2]
      const explanation = rest[rest.length - 1]

      const safeTrim = (value: unknown) => {
        if (typeof value === 'string') return value.trim()
        if (value === null || value === undefined) return ''
        return String(value).trim()
      }

      let quizText = `❓ QUIZ QUESTION\nQuestion: ${safeTrim(question)}\n\nOptions:\n`
      options.forEach((opt: string, index: number) => {
        quizText += `${index + 1}. ${safeTrim(opt)}\n`
      })
      quizText += `\nAnswer: ${safeTrim(correctAnswer)}\n`

      if (explanation) {
        quizText += `Explanation: ${safeTrim(explanation)}\n`
      }

      return quizText + '\n'
    }
  )

  // More robust quiz pattern that handles variable number of options
  transformedContent = transformedContent.replace(/\[QUIZ\](.*?)\[\/QUIZ\]/gs, (match) => {
    try {
      // Extract content between tags
      const content = match.replace(/\[QUIZ\]|\[\/QUIZ\]/g, '')

      // Split by pipe character to get components
      const parts = content.split('|')

      // Need at least question + 2 options + answer
      if (parts.length < 4) return match

      const question = parts[0].trim()

      // The last two parts are typically the answer and explanation
      const explanation = parts[parts.length - 1].trim()
      const correctAnswer = parts[parts.length - 2].trim()

      // Everything in between is options
      const options = parts.slice(1, parts.length - 2)

      let quizText = `❓ QUIZ QUESTION\nQuestion: ${question}\n\nOptions:\n`
      options.forEach((opt, index) => {
        quizText += `${index + 1}. ${opt.trim()}\n`
      })

      quizText += `\nAnswer: ${correctAnswer}\n`

      if (explanation && explanation !== correctAnswer) {
        quizText += `Explanation: ${explanation}\n`
      }

      return quizText + '\n'
    } catch (e) {
      // If parsing fails, return the original match
      return match
    }
  })

  // Process quiz sessions - [QUIZ_SESSION:id:title]...[/QUIZ_SESSION]
  transformedContent = transformedContent.replace(
    /\[QUIZ_SESSION:(.*?):(.*?)\](.*?)\[\/QUIZ_SESSION\]/gs,
    (_, id, title, content) => {
      let quizSessionText = `📚 QUIZ: ${title.trim()}\n\n`

      // Extract individual quiz items
      const quizItems = Array.from(
        content.matchAll(/\[QUIZ_ITEM\](.*?)\[\/QUIZ_ITEM\]/gs)
      ) as RegExpMatchArray[]

      if (quizItems.length === 0) {
        // If no quiz items found, try to parse entire content as a quiz
        try {
          const contentLines = content.split('\n').filter((line: string) => line.trim())
          if (contentLines.length > 0) {
            quizSessionText += `Questions:\n\n`

            // Simple parsing for basic quiz format
            let currentQuestion = ''
            let currentOptions: string[] = []
            let currentAnswer = ''

            contentLines.forEach((line: string) => {
              const trimmedLine = line.trim()
              if (trimmedLine.startsWith('Question:')) {
                // Start a new question
                if (currentQuestion) {
                  // Add previous question
                  quizSessionText += `Q: ${currentQuestion}\n`
                  currentOptions.forEach((opt, idx) => {
                    quizSessionText += `   ${String.fromCharCode(97 + idx)}. ${opt}\n`
                  })
                  if (currentAnswer) {
                    quizSessionText += `   Answer: ${currentAnswer}\n\n`
                  }
                }
                currentQuestion = trimmedLine.replace('Question:', '').trim()
                currentOptions = []
                currentAnswer = ''
              } else if (trimmedLine.startsWith('Option:') || /^[A-D][\:\.\)]/.test(trimmedLine)) {
                // Add option
                const option = trimmedLine.replace(/^(Option:|[A-D][\:\.\)])\s*/, '').trim()
                currentOptions.push(option)
              } else if (trimmedLine.startsWith('Answer:')) {
                // Add answer
                currentAnswer = trimmedLine.replace('Answer:', '').trim()
              }
            })

            // Add the last question
            if (currentQuestion) {
              quizSessionText += `Q: ${currentQuestion}\n`
              currentOptions.forEach((opt, idx) => {
                quizSessionText += `   ${String.fromCharCode(97 + idx)}. ${opt}\n`
              })
              if (currentAnswer) {
                quizSessionText += `   Answer: ${currentAnswer}\n\n`
              }
            }
          }
        } catch (e) {
          quizSessionText += 'Failed to parse quiz content.\n\n'
        }

        return quizSessionText
      }

      quizItems.forEach((match, idx) => {
        const quizItemText = match[1]
        const itemParts = quizItemText.split('|')

        if (itemParts.length < 4) return ''

        const question = itemParts[0].trim()
        const options = itemParts.slice(1, -2)
        const correctAnswer = itemParts[itemParts.length - 2].trim()
        const explanation = itemParts[itemParts.length - 1].trim()

        quizSessionText += `Question ${idx + 1}: ${question}\n`
        options.forEach((opt: string, i: number) => {
          quizSessionText += `${String.fromCharCode(97 + i)}. ${opt.trim()}\n`
        })
        quizSessionText += `Answer: ${correctAnswer}\n`

        if (explanation) {
          quizSessionText += `Explanation: ${explanation}\n`
        }

        quizSessionText += '\n'
      })

      return quizSessionText
    }
  )

  // Process timelines - [TIMELINE]{...}[/TIMELINE]
  transformedContent = transformedContent.replace(
    /\[TIMELINE\](.*?)\[\/TIMELINE\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let timelineText = `📅 TIMELINE\n\n`

        if (data.events && Array.isArray(data.events)) {
          data.events.forEach((event: any, index: number) => {
            timelineText += `${index + 1}. ${event.date || 'N/A'}: ${event.title || 'Untitled Event'}`
            if (event.description) {
              timelineText += `\n   ${event.description}`
            }
            timelineText += '\n\n'
          })
        }

        return timelineText
      } catch (e) {
        return 'TIMELINE (unable to parse data)\n\n'
      }
    }
  )

  // Process code diffs - [CODE_DIFF]{...}[/CODE_DIFF]
  transformedContent = transformedContent.replace(
    /\[CODE_DIFF\](.*?)\[\/CODE_DIFF\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let diffText = `📝 CODE DIFF\n\n`

        if (data.title) {
          diffText += `${data.title}\n\n`
        }

        if (data.beforeCode) {
          diffText += `--- BEFORE ---\n${data.beforeCode}\n\n`
        }

        if (data.afterCode) {
          diffText += `+++ AFTER +++\n${data.afterCode}\n\n`
        }

        return diffText
      } catch (e) {
        return 'CODE DIFF (unable to parse data)\n\n'
      }
    }
  )

  // Process polls - [POLL]{...}[/POLL]
  transformedContent = transformedContent.replace(/\[POLL\](.*?)\[\/POLL\]/gs, (_, jsonData) => {
    try {
      const data = JSON.parse(jsonData)
      let pollText = `📊 POLL: ${data.question || 'Untitled Poll'}\n\n`

      if (data.options && Array.isArray(data.options)) {
        data.options.forEach((option: any, index: number) => {
          pollText += `${index + 1}. ${option.text || option}\n`
        })
      }

      return pollText + '\n'
    } catch (e) {
      return 'POLL (unable to parse data)\n\n'
    }
  })

  // Process definition cards - [DEFINITION]term|definition[EXAMPLES]example1|example2[/EXAMPLES][RELATED]term1>link1|term2[/RELATED][/DEFINITION]
  transformedContent = transformedContent.replace(
    /\[DEFINITION\](.*?)\[\/DEFINITION\]/gs,
    (_, content) => {
      // Extract term and definition
      const mainParts = content.split('[EXAMPLES]')[0].split('|')
      const term = mainParts[0].trim()
      const definition = mainParts[1]?.trim() || ''

      let definitionText = `📖 DEFINITION: ${term}\n${definition}\n\n`

      // Extract examples if present
      const examplesMatch = content.match(/\[EXAMPLES\](.*?)\[\/EXAMPLES\]/s)
      if (examplesMatch && examplesMatch[1]) {
        definitionText += 'Examples:\n'
        examplesMatch[1].split('|').forEach((example: string) => {
          definitionText += `• ${example.trim()}\n`
        })
        definitionText += '\n'
      }

      // Extract related terms if present
      const relatedMatch = content.match(/\[RELATED\](.*?)\[\/RELATED\]/s)
      if (relatedMatch && relatedMatch[1]) {
        definitionText += 'Related Terms:\n'
        relatedMatch[1].split('|').forEach((related: string) => {
          const parts = related.split('>')
          definitionText += `• ${parts[0].trim()}\n`
        })
      }

      return definitionText + '\n'
    }
  )

  // Process code snippets - [CODE_SNIPPET]{...}[/CODE_SNIPPET]
  transformedContent = transformedContent.replace(
    /\[CODE_SNIPPET\](.*?)\[\/CODE_SNIPPET\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let snippetText = ''

        if (data.title) {
          snippetText += `${data.title}\n\n`
        }

        if (data.description) {
          snippetText += `${data.description}\n\n`
        }

        if (data.code) {
          snippetText += `CODE (${data.language || 'code'}):\n${data.code}\n\n`
        }

        return snippetText
      } catch (e) {
        return 'CODE SNIPPET (unable to parse data)\n\n'
      }
    }
  )

  // Process tabbed content - [TABS]{...}[/TABS]
  transformedContent = transformedContent.replace(/\[TABS\](.*?)\[\/TABS\]/gs, (_, jsonData) => {
    try {
      const data = JSON.parse(jsonData)
      let tabsText = `${data.title || 'Tabbed Content'}\n\n`

      if (data.tabs && Array.isArray(data.tabs)) {
        data.tabs.forEach((tab: any) => {
          tabsText += `=== ${tab.title || 'Tab'} ===\n${tab.content || ''}\n\n`
        })
      }

      return tabsText
    } catch (e) {
      return 'TABBED CONTENT (unable to parse data)\n\n'
    }
  })

  // Process checklist - [CHECKLIST]{...}[/CHECKLIST]
  transformedContent = transformedContent.replace(
    /\[CHECKLIST\](.*?)\[\/CHECKLIST\]/gs,
    (_, rawContent) => {
      try {
        const data = parseChecklistContent(rawContent)
        return checklistToMarkdown(data)
      } catch (e) {
        return 'CHECKLIST (unable to parse data)\n\n'
      }
    }
  )

  // Process mind maps - [MIND_MAP]{...}[/MIND_MAP]
  transformedContent = transformedContent.replace(
    /\[MIND_MAP\](.*?)\[\/MIND_MAP\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let mindMapText = `🧠 MIND MAP: ${data.title || 'Untitled'}\n\n`

        // Simple representation of mind map structure
        const processNode = (node: any, level = 0) => {
          if (!node) return ''

          let nodeText = '  '.repeat(level) + `• ${node.text || 'Unnamed node'}\n`

          if (node.children && Array.isArray(node.children)) {
            node.children.forEach((child: any) => {
              nodeText += processNode(child, level + 1)
            })
          }

          return nodeText
        }

        if (data.rootNode) {
          mindMapText += processNode(data.rootNode)
        }

        return mindMapText + '\n'
      } catch (e) {
        return 'MIND MAP (unable to parse data)\n\n'
      }
    }
  )

  // Process flow charts - [FLOW_CHART]{...}[/FLOW_CHART]
  transformedContent = transformedContent.replace(
    /\[FLOW_CHART\](.*?)\[\/FLOW_CHART\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let flowChartText = `📊 FLOW CHART: ${data.title || 'Untitled'}\n\n`

        // Simple text representation of nodes and connections
        if (data.nodes && Array.isArray(data.nodes)) {
          // First list all nodes
          flowChartText += 'Nodes:\n'
          data.nodes.forEach((node: any) => {
            flowChartText += `• ${node.id}: ${node.label || 'Unnamed'}`
            if (node.description) {
              flowChartText += ` (${node.description})`
            }
            flowChartText += '\n'
          })

          // Then list connections
          flowChartText += '\nConnections:\n'
          data.nodes.forEach((node: any) => {
            if (node.targets && Array.isArray(node.targets) && node.targets.length > 0) {
              flowChartText += `• ${node.id} → ${node.targets.join(', ')}\n`
            }
          })
        }

        return flowChartText + '\n'
      } catch (e) {
        return 'FLOW CHART (unable to parse data)\n\n'
      }
    }
  )

  // Process data dashboards - [DATA_DASHBOARD]{...}[/DATA_DASHBOARD]
  transformedContent = transformedContent.replace(
    /\[DATA_DASHBOARD\](.*?)\[\/DATA_DASHBOARD\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let dashboardText = `📊 DASHBOARD: ${data.title || 'Untitled'}\n\n`

        if (data.description) {
          dashboardText += `${data.description}\n\n`
        }

        // Add metrics
        if (data.metrics && Array.isArray(data.metrics)) {
          dashboardText += 'KEY METRICS:\n'
          data.metrics.forEach((metric: any) => {
            if (metric.type === 'section_header') {
              dashboardText += `\n=== ${metric.label} ===\n`
              if (metric.value) {
                dashboardText += `${metric.value}\n`
              }
            } else {
              dashboardText += `• ${metric.label}: ${metric.value}`
              if (metric.status === 'positive') {
                dashboardText += ' ✅'
              } else if (metric.status === 'negative') {
                dashboardText += ' ❌'
              } else if (metric.status === 'warning') {
                dashboardText += ' ⚠️'
              }
              dashboardText += '\n'
            }
          })
          dashboardText += '\n'
        }

        // Add chart descriptions
        if (data.charts && Array.isArray(data.charts)) {
          dashboardText += 'CHARTS:\n'
          data.charts.forEach((chart: any) => {
            dashboardText += `• ${chart.title || 'Untitled Chart'}`
            if (chart.description) {
              dashboardText += `: ${chart.description}`
            }
            dashboardText += '\n'
          })
        }

        return dashboardText + '\n'
      } catch (e) {
        return 'DASHBOARD (unable to parse data)\n\n'
      }
    }
  )

  // Process business canvas - [BUSINESS_CANVAS]{...}[/BUSINESS_CANVAS]
  transformedContent = transformedContent.replace(
    /\[BUSINESS_CANVAS\](.*?)\[\/BUSINESS_CANVAS\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let canvasText = `💼 BUSINESS MODEL CANVAS: ${data.title || 'Untitled'}\n\n`

        if (data.sections && Array.isArray(data.sections)) {
          data.sections.forEach((section: any) => {
            canvasText += `=== ${section.title || 'Section'} ===\n`

            if (Array.isArray(section.content)) {
              section.content.forEach((item: string) => {
                canvasText += `• ${item}\n`
              })
            } else if (typeof section.content === 'string') {
              canvasText += `${section.content}\n`
            }

            canvasText += '\n'
          })
        } else if (data.sections && typeof data.sections === 'object') {
          // Handle case where sections is an object
          Object.entries(data.sections).forEach(([key, content]: [string, any]) => {
            // Convert key to title
            const title = key.replace(/([A-Z])/g, ' $1').trim()
            canvasText += `=== ${title} ===\n`

            if (Array.isArray(content)) {
              content.forEach((item: string) => {
                canvasText += `• ${item}\n`
              })
            } else if (typeof content === 'string') {
              canvasText += `${content}\n`
            }

            canvasText += '\n'
          })
        }

        return canvasText
      } catch (e) {
        return 'BUSINESS MODEL CANVAS (unable to parse data)\n\n'
      }
    }
  )

  // Process complex specialized components with simpler representations

  // Citation Network
  transformedContent = transformedContent.replace(
    /\[CITATION_NETWORK\](.*?)\[\/CITATION_NETWORK\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let networkText = `📚 CITATION NETWORK: ${data.title || 'Untitled'}\n\n`

        if (data.nodes && Array.isArray(data.nodes)) {
          networkText += 'KEY PUBLICATIONS:\n'
          data.nodes.forEach((node: any) => {
            networkText += `• ${node.title || node.id || 'Unnamed'}`
            if (node.authors) {
              networkText += ` (${Array.isArray(node.authors) ? node.authors.join(', ') : node.authors})`
            }
            if (node.year) {
              networkText += `, ${node.year}`
            }
            networkText += '\n'
          })
        }

        return networkText + '\n'
      } catch (e) {
        return 'CITATION NETWORK (unable to parse data)\n\n'
      }
    }
  )

  // Stakeholder Map
  transformedContent = transformedContent.replace(
    /\[STAKEHOLDER_MAP\](.*?)\[\/STAKEHOLDER_MAP\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let mapText = `👥 STAKEHOLDER MAP: ${data.title || 'Untitled'}\n\n`

        if (data.stakeholders && Array.isArray(data.stakeholders)) {
          // Group stakeholders by category
          const categories: Record<string, any[]> = {}

          data.stakeholders.forEach((stakeholder: any) => {
            const category = stakeholder.category || 'Uncategorized'
            if (!categories[category]) {
              categories[category] = []
            }
            categories[category].push(stakeholder)
          })

          // Output stakeholders by category
          Object.entries(categories).forEach(([category, stakeholders]) => {
            mapText += `=== ${category} ===\n`
            stakeholders.forEach((stakeholder: any) => {
              mapText += `• ${stakeholder.name || 'Unnamed'}`
              if (stakeholder.influence) {
                mapText += ` (Influence: ${stakeholder.influence})`
              }
              if (stakeholder.interest) {
                mapText += ` (Interest: ${stakeholder.interest})`
              }
              mapText += '\n'
            })
            mapText += '\n'
          })
        }

        return mapText
      } catch (e) {
        return 'STAKEHOLDER MAP (unable to parse data)\n\n'
      }
    }
  )

  // Scenario Planner
  transformedContent = transformedContent.replace(
    /\[SCENARIO_PLANNER\](.*?)\[\/SCENARIO_PLANNER\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let plannerText = `🔮 SCENARIO PLANNING: ${data.title || 'Untitled'}\n\n`

        if (data.scenarios && Array.isArray(data.scenarios)) {
          data.scenarios.forEach((scenario: any, index: number) => {
            plannerText += `=== Scenario ${index + 1}: ${scenario.name || 'Unnamed'} ===\n`

            if (scenario.description) {
              plannerText += `${scenario.description}\n\n`
            }

            if (scenario.likelihood) {
              plannerText += `Likelihood: ${scenario.likelihood}\n`
            }

            if (scenario.impact) {
              plannerText += `Impact: ${scenario.impact}\n`
            }

            if (scenario.factors && Array.isArray(scenario.factors)) {
              plannerText += '\nKey Factors:\n'
              scenario.factors.forEach((factor: any) => {
                plannerText += `• ${factor.name || factor}\n`
              })
            }

            if (scenario.actions && Array.isArray(scenario.actions)) {
              plannerText += '\nRecommended Actions:\n'
              scenario.actions.forEach((action: any) => {
                plannerText += `• ${action.name || action}\n`
              })
            }

            plannerText += '\n'
          })
        }

        return plannerText
      } catch (e) {
        return 'SCENARIO PLANNING (unable to parse data)\n\n'
      }
    }
  )

  // Market Positioner
  transformedContent = transformedContent.replace(
    /\[MARKET_POSITIONER\](.*?)\[\/MARKET_POSITIONER\]/gs,
    (_, jsonData) => {
      try {
        const data = JSON.parse(jsonData)
        let positionerText = `📊 MARKET POSITIONING: ${data.title || 'Untitled'}\n\n`

        if (data.description) {
          positionerText += `${data.description}\n\n`
        }

        if (data.dimensions && Array.isArray(data.dimensions) && data.dimensions.length >= 2) {
          positionerText += `Dimensions: ${data.dimensions[0].label || 'X'} vs ${data.dimensions[1].label || 'Y'}\n\n`
        }

        if (data.entities && Array.isArray(data.entities)) {
          positionerText += 'Market Players:\n'
          data.entities.forEach((entity: any) => {
            positionerText += `• ${entity.name || 'Unnamed'}`

            if (entity.description) {
              positionerText += `: ${entity.description}`
            }

            if (entity.x !== undefined && entity.y !== undefined) {
              positionerText += ` (Position: ${entity.x.toFixed(1)}, ${entity.y.toFixed(1)})`
            }

            positionerText += '\n'
          })
        }

        return positionerText + '\n'
      } catch (e) {
        return 'MARKET POSITIONING (unable to parse data)\n\n'
      }
    }
  )

  // Process HTML tables - convert <table> elements to markdown tables
  transformedContent = transformedContent.replace(
    /<table[^>]*>([\s\S]*?)<\/table>/gi,
    (_, tableContent) => {
      try {
        // Extract table headers
        const headerMatch = tableContent.match(/<thead[^>]*>([\s\S]*?)<\/thead>/i)
        const bodyMatch = tableContent.match(/<tbody[^>]*>([\s\S]*?)<\/tbody>/i)

        let markdownTable = '\n'

        if (headerMatch) {
          // Process headers
          const headerRows = headerMatch[1].match(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)
          if (headerRows && headerRows.length > 0) {
            const headerCells = headerRows[0].match(/<th[^>]*>([\s\S]*?)<\/th>/gi)
            if (headerCells) {
              const headers = headerCells.map((cell: string) => cell.replace(/<[^>]+>/g, '').trim())
              markdownTable += '| ' + headers.join(' | ') + ' |\n'
              markdownTable += '|' + headers.map(() => ' --- ').join('|') + '|\n'
            }
          }
        }

        // Process body rows (or all rows if no thead)
        const bodyContent = bodyMatch ? bodyMatch[1] : tableContent
        const bodyRows = bodyContent.match(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)

        if (bodyRows) {
          bodyRows.forEach((row: string) => {
            const cells = row.match(/<(td|th)[^>]*>([\s\S]*?)<\/(td|th)>/gi)
            if (cells) {
              const cellContents = cells.map((cell: string) => cell.replace(/<[^>]+>/g, '').trim())
              markdownTable += '| ' + cellContents.join(' | ') + ' |\n'
            }
          })
        }

        return markdownTable + '\n'
      } catch (e) {
        // If parsing fails, return a simple table indicator
        return '\n[TABLE - unable to parse]\n\n'
      }
    }
  )

  return transformedContent
}
